import type {
  ListUpstreamCreditTermsResponse,
  PutUpstreamCreditTermsRequest,
  PutUpstreamCreditTermsResponse,
  UpstreamCreditTermsRecord,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * React Query wrappers for `/v1/ops/upstreams/:id/credit-terms` — postpaid
 * billing mode and CNY credit limit per upstream account (#2756).
 *
 * Query key: `['credit-terms', upstreamId]`. A PUT invalidates it; the
 * funding-batch list is untouched because terms do not move batches.
 */

const UPSTREAM_BASE = '/v1/ops/upstreams';

export function creditTermsQueryKey(upstreamId: string): readonly ['credit-terms', string] {
  return ['credit-terms', upstreamId] as const;
}

export async function listCreditTerms(
  upstreamId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<readonly UpstreamCreditTermsRecord[]> {
  const response = await opsApiRequest<ListUpstreamCreditTermsResponse>(
    `${UPSTREAM_BASE}/${encodeURIComponent(upstreamId)}/credit-terms`,
    { signal },
    fetchImpl,
  );
  return response.terms;
}

export async function putCreditTerms(
  upstreamId: string,
  body: PutUpstreamCreditTermsRequest,
  fetchImpl: typeof fetch,
): Promise<PutUpstreamCreditTermsResponse> {
  return opsApiRequest<PutUpstreamCreditTermsResponse>(
    `${UPSTREAM_BASE}/${encodeURIComponent(upstreamId)}/credit-terms`,
    { method: 'PUT', body },
    fetchImpl,
  );
}

export function useCreditTerms(
  upstreamId: string,
): UseQueryResult<readonly UpstreamCreditTermsRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: creditTermsQueryKey(upstreamId),
    queryFn: ({ signal }) => listCreditTerms(upstreamId, fetchImpl, signal),
    enabled: upstreamId.length > 0,
  });
}

export function usePutCreditTerms(
  upstreamId: string,
): UseMutationResult<PutUpstreamCreditTermsResponse, Error, PutUpstreamCreditTermsRequest> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => putCreditTerms(upstreamId, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: creditTermsQueryKey(upstreamId) });
    },
  });
}
